// Global Instructions Rule Applied!
//
// Single-deal execution state for the deal form's Execution tab: the deal's
// execution row (same read as the board, filtered), stage history on demand,
// stage moves via the existing pipeline-deals PATCH, and checklist toggles.
import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { syncUpdatesToState } from '../../../state_manager/sync_engine/controller.ts'
import {
  DealExecutionBoardRecord,
  DealExecutionContext,
  DealExecutionStageHistoryEntry
} from '../../../state_manager/data_models/deal_execution_board_model.ts'
import {
  deleteRoundInstrument,
  deleteRoundParticipation,
  saveRoundParticipation,
  enterExecution,
  fetchExecutionBoard,
  fetchExecutionContext,
  fetchStageHistory,
  moveExecutionStage,
  patchDealRound,
  patchRound,
  saveRoundInstrument,
  setChecklistAnswer
} from '../services/dealExecutionService.ts'

export const useDealExecutionState = (dealId: string | null) => {
  const [row, setRow] = useState<DealExecutionBoardRecord | null>(null)
  const [context, setContext] = useState<DealExecutionContext | null>(null)
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<DealExecutionStageHistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const refetch = useCallback(async () => {
    if (!dealId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const [boardResult, contextResult] = await Promise.all([
        fetchExecutionBoard(token, { dealId }),
        fetchExecutionContext(token, dealId)
      ])
      setRow(boardResult.data[0] ?? null)
      setContext(contextResult.data)
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => {
    refetch()
  }, [refetch])

  const loadHistory = useCallback(async () => {
    if (!dealId) return
    setHistoryLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchStageHistory(token, dealId)
      setHistory(result.data)
    } finally {
      setHistoryLoading(false)
    }
  }, [dealId])

  /** Move the execution membership to another pipeline_config (Next/Back).
   *  Advances silently — answers never gate stage moves (signed-off v1).
   *  The move RPC stamps stage_date and drops the deal at the TOP of the
   *  target kanban column (owner 2026-08-26). */
  const moveToStage = useCallback(
    async (pipelineConfigId: string): Promise<boolean> => {
      if (!row) return false
      const token = getAccessToken(store.getState().auth)
      const result = await moveExecutionStage(token, row.pipeline_deals__id, pipelineConfigId)
      await refetch()
      return result.success
    },
    [row, refetch]
  )

  const toggleChecklistItem = useCallback(
    async (checklistItemId: string, answer: boolean) => {
      if (!row || !dealId) return
      setRow((prev) =>
        prev
          ? {
              ...prev,
              prog__checklist: (prev.prog__checklist || []).map((item) =>
                item.id === checklistItemId ? { ...item, answer } : item
              ),
              // Items in any stage can be answered ahead of time — keep the
              // per-stage map in sync so the modal reflects the toggle at once.
              prog__checklists_all: Object.fromEntries(
                Object.entries(prev.prog__checklists_all || {}).map(([cfgId, items]) => [
                  cfgId,
                  items.map((item) => (item.id === checklistItemId ? { ...item, answer } : item))
                ])
              )
            }
          : prev
      )
      const token = getAccessToken(store.getState().auth)
      const result = await setChecklistAnswer(token, dealId, checklistItemId, answer)
      if (result.error) {
        await refetch()
      } else {
        await refetch() // pick up audited who/when stamps
      }
    },
    [row, dealId, refetch]
  )

  /**
   * Save round fields (shared entity — affects every deal on the round). The
   * deal records in Redux carry the round's columns (rounds__*) too - the
   * Detail tab, the deal header and the grids read them - so the saved
   * values go there as well, exactly as an inline edit on the Detail tab
   * does; otherwise the edit looks ignored everywhere but this tab.
   */
  const patchRoundFields = useCallback(
    async (updates: { id: string } & Record<string, unknown>): Promise<boolean> => {
      const token = getAccessToken(store.getState().auth)
      const result = await patchRound(token, updates)
      if (result.success && result.data) syncUpdatesToState(result.data)
      await refetch()
      return result.success
    },
    [refetch]
  )

  /** Link (or unlink with null) one of the company's rounds to this deal. */
  const linkRound = useCallback(
    async (roundId: string | null): Promise<boolean> => {
      if (!dealId) return false
      const token = getAccessToken(store.getState().auth)
      const result = await patchDealRound(token, dealId, roundId)
      await refetch()
      return result.success
    },
    [dealId, refetch]
  )

  /** Insert (instrumentId null) or update a secondary/converting instrument. */
  const saveInstrument = useCallback(
    async (instrumentId: string | null, fields: Record<string, unknown>): Promise<boolean> => {
      const roundId = context?.round?.rounds__id
      if (!roundId) return false
      const token = getAccessToken(store.getState().auth)
      const result = await saveRoundInstrument(token, roundId, instrumentId, fields)
      await refetch()
      return result.success
    },
    [context, refetch]
  )

  /** Soft-delete one round instrument. */
  const removeInstrument = useCallback(
    async (instrumentId: string): Promise<boolean> => {
      const token = getAccessToken(store.getState().auth)
      const result = await deleteRoundInstrument(token, instrumentId)
      await refetch()
      return result.success
    },
    [refetch]
  )

  /** Insert (participationId null) or update a round co-investor participation. */
  const saveParticipation = useCallback(
    async (participationId: string | null, fields: Record<string, unknown>): Promise<boolean> => {
      const roundId = context?.round?.rounds__id
      if (!roundId) return false
      const token = getAccessToken(store.getState().auth)
      const result = await saveRoundParticipation(token, roundId, participationId, fields)
      await refetch()
      return result.success
    },
    [context, refetch]
  )

  /** Soft-delete one round participation. */
  const removeParticipation = useCallback(
    async (participationId: string): Promise<boolean> => {
      const token = getAccessToken(store.getState().auth)
      const result = await deleteRoundParticipation(token, participationId)
      await refetch()
      return result.success
    },
    [refetch]
  )

  /** Manual promotion into the execution pipeline (idempotent server-side). */
  const enterExecutionManually = useCallback(async (): Promise<boolean> => {
    if (!dealId) return false
    const token = getAccessToken(store.getState().auth)
    const result = await enterExecution(token, dealId)
    await refetch()
    return result.success
  }, [dealId, refetch])

  return {
    row,
    context,
    loading,
    history,
    historyLoading,
    loadHistory,
    moveToStage,
    toggleChecklistItem,
    enterExecutionManually,
    patchRoundFields,
    linkRound,
    saveInstrument,
    removeInstrument,
    saveParticipation,
    removeParticipation,
    refetch
  }
}
